//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useQuery } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useMemo } from 'react';

import PluginPanelBody from './PluginPanelBody';
import { panelIdForRow, toPanelSpec } from './pluginPanelSpec';
import {
  pluginSurfaceRowsQuery, type PluginSurfaceRow
} from '@/components/Header/pluginSurfaces';
import { useAuth } from '@/hooks/useAuth';
import { usePanels } from '@/hooks/usePanels';
import { type PluginWebviewSurface } from '@/services/apiTypes';

/**
 * Registers one plugin surface as a panel.  A component per surface because
 * registration is an effect and effects cannot be called in a loop.
 *
 * Registration is all it does: whether the panel is then restored from the
 * session or preloaded is decided by the registry, from the spec below.
 */
const PluginPanelClient = ({ row }: { row: PluginSurfaceRow }) => {
  const { registerPanel, unregisterPanel } = usePanels();

  const spec = useMemo(
    () => toPanelSpec(row, () => (
      // A row reaches here only if it declares `form: 'panel'`, and
      // `FORM_KINDS` allows that form on webview surfaces alone.
      <PluginPanelBody
        surface={row.surface as PluginWebviewSurface}
        pluginName={row.plugin.name}
      />
    )),
    [row]);

  // Before paint, not after: a pill draws the panel's icon out of its
  // registration, so a passive effect would leave the rail a frame behind
  // and the pills would visibly grow their icons.
  useLayoutEffect(() => {
    registerPanel(spec);
  }, [registerPanel, spec]);

  // Withdraw on unmount alone, not on every spec identity change -- a
  // re-registration must not bounce the panel's iframe. This client unmounts
  // when its surface leaves the surfaces payload, which is how an admin
  // disable arrives, and the registration must not outlive the surface: the
  // launcher and Tools row vanish with the row, so a panel left mounted
  // would be a live frame of a withdrawn surface that nothing can reach.
  useEffect(
    () => () => unregisterPanel(spec.id), [unregisterPanel, spec.id]);

  return null;
};

/**
 * Registers every plugin surface that declares `form: 'panel'` with the
 * panel registry.
 *
 * Mounted beside the host rather than inside the header, for the same reason
 * the chat client is: the header remounts on every route change, and a
 * registrar that came and went with it would take its panels' iframes down
 * on each navigation.
 *
 * Which surfaces those are is asked of `panelIdForRow`, the same function the
 * header routes on, so a surface cannot be registered here as a panel while
 * the header opens it as a modal -- a split that would leave a panel on the
 * rail with no pill and no menu row to open it.
 *
 * Being a sibling of the router is also why the query is gated on a signed-in
 * user, the way the header's copy of it is.  Nothing above this unmounts on
 * the public routes, so an ungated read of an authenticated endpoint 401s on
 * the login page -- and a 401 on any query is what the app treats as an
 * expired session, so it would clear the cache and redirect a visitor off
 * Terms of Use.
 */
const PluginPanelClients = () => {
  const { activeUser } = useAuth();
  const { data, error } = useQuery({
    ...pluginSurfaceRowsQuery(), enabled: !!activeUser
  });

  // A rejected manifest arrives as the shared query's error: rethrown here,
  // inside PanelErrorBoundary, it is shown loudly at the cost of the panels
  // alone. The header consumes the same query and cannot throw on it, so a
  // bad manifest no longer blanks the routes it renders inside of.
  if (error) throw error;

  // Row identity has to be stable across renders, since each row is a
  // registration's dependency; the query's select keeps it so.
  const rows = useMemo(
    () => (data ?? []).filter((row) => panelIdForRow(row) !== null),
    [data]);

  return rows.map((row) => (
    <PluginPanelClient key={`${row.plugin.name}:${row.key}`} row={row} />
  ));
};

export default PluginPanelClients;
